import classNames from "classnames";
import { useTranslations } from "next-intl";
import {
  CSSProperties,
  DetailedHTMLProps,
  forwardRef,
  SelectHTMLAttributes,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import Select from "./select";
import styles from "./select-generic.module.scss";

export type SelectOption = { value: string | number; label: string };

type SelectProps = DetailedHTMLProps<SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement> & {
  label: string;
  options: SelectOption[];
  isOptional?: boolean;
  error?: string;
  onClearErrors?: () => void;
};

export default forwardRef<HTMLSelectElement, SelectProps>(function SelectGeneric(
  { label, options, isOptional, error, onClearErrors, className, defaultValue, ...rest },
  ref
) {
  const t = useTranslations();
  const id = useId();
  const [isInputFocus, setInputFocus] = useState(false);

  //the width of the label that will be used to create a transparent label is kept track of
  const refLabel = useRef<HTMLLabelElement>(null);
  const [widthLabel, setWidthLabel] = useState<number | undefined>();

  const styleLabelLine = {
    "--label-line-width": `calc(100% - ${widthLabel}px - var(--label-external-padding-horizontal))`,
  } as CSSProperties;

  useEffect(() => {
    setWidthLabel(refLabel.current?.offsetWidth);
  }, [isOptional]);

  return (
    <div
      className={classNames(
        styles.container,
        error ? styles.containerWithErrors : null,
        isInputFocus ? styles.containerInFocus : null,
        rest.hidden ? styles.hidden : null,
        className
      )}
    >
      <div className={styles.wrapperLabel}>
        <label
          ref={refLabel}
          htmlFor={id}
          onMouseDown={(e) => e.preventDefault()}
          style={styleLabelLine}
          className={styles.label}
        >
          {label}
          {isOptional ? (
            ""
          ) : (
            <span className={styles.mandatory} aria-hidden>
              {t("generic.form.mandatory_short")}
            </span>
          )}
        </label>
      </div>
      <Select
        id={id}
        options={options}
        onClearErrors={onClearErrors}
        setInputFocus={setInputFocus}
        className={styles.select}
        {...rest}
        ref={ref}
        defaultValue={defaultValue}
      />
      {error && (
        <p id={`${id}-error`} className={styles.errorMessage}>
          {error}
        </p>
      )}
    </div>
  );
});
